import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useUser } from '@/features/auth/hooks/use-user';
import { User } from '@/features/auth/types/user';
import { GetOrgInvitationResult } from '@/features/org/api/get-org-invitation';
import { useSetCurrentOrgId } from '@/features/org/hooks/use-current-org-id';
import { useGetOrgInvitation } from '@/features/org/hooks/use-get-org-invitation';
import { useManageOrgInvitationMutation } from '@/features/org/hooks/use-manage-org-invitation-mutation';
import { getUserOrgsQueryKey } from '@/features/org/hooks/use-user-orgs';
import { OrgId } from '@/features/org/types/org-id';
import {
  FlowLayout,
  FlowLayoutBody,
} from '@/shared/components/flow/flow-layout';
import { Background } from '@/shared/components/layouts/background';
import { LogoWithName } from '@/shared/components/theme/logo-with-name';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Spinner } from '@/shared/components/ui/spinner';
import { getInitials } from '@/shared/utils/avatar-util';

interface InvitationError {
  title: string;
  message: string;
}

interface InvitationCardProps {
  title: string;
  children: React.ReactNode;
  isError?: boolean;
}

const InvitationCard = ({
  title,
  children,
  isError = false,
}: InvitationCardProps) => {
  return (
    <Card variant={'styled'}>
      <CardHeader className="bg-muted/20">
        <CardTitle
          className={`text-center ${isError ? 'text-destructive' : ''}`}
        >
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4 pt-6">
        {children}
      </CardContent>
    </Card>
  );
};

const ErrorCard = ({ title, message }: InvitationError) => {
  const navigate = useNavigate();
  const { t } = useTranslation('org', { keyPrefix: 'orgInvitation' });

  return (
    <InvitationCard title={title} isError>
      <p className="text-center text-destructive">{message}</p>
      <Button onClick={() => navigate(paths.home.getHref())} variant="outline">
        {t('goHome')}
      </Button>
    </InvitationCard>
  );
};

const LoadingCard = () => {
  const { t } = useTranslation('org', { keyPrefix: 'orgInvitation' });

  return (
    <InvitationCard title={t('processingTitle')}>
      <Spinner size="lg" />
      <p className="text-center text-muted-foreground">
        {t('processingMessage')}
      </p>
    </InvitationCard>
  );
};

const InvitationActions = ({
  invitation,
  user,
  invitationToken,
}: {
  invitation: GetOrgInvitationResult;
  user: User;
  invitationToken: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('org', { keyPrefix: 'orgInvitation' });
  const setCurrentOrgId = useSetCurrentOrgId();
  const queryClient = useQueryClient();
  const { mutate } = useManageOrgInvitationMutation({
    onSuccess: async (_, variables) => {
      if (variables.status === 'accepted' && invitation.org?.id) {
        await queryClient.invalidateQueries({
          queryKey: getUserOrgsQueryKey(user?.id),
        });
        setCurrentOrgId(invitation.org.id as OrgId);
        navigate(paths.dashboard.getHref());
      } else {
        navigate(paths.home.getHref());
      }
    },
  });

  const handleAccept = () => mutate({ invitationToken, status: 'accepted' });
  const handleDecline = () => mutate({ invitationToken, status: 'declined' });

  return (
    <Card className="max-w-[814px]" variant={'form'} wrapWithStars>
      <CardHeader className="items-center space-y-14">
        {invitation.org && (
          <Avatar className="size-24">
            <AvatarImage
              src={invitation.org?.image_url || ''}
              alt={invitation.org?.name || ''}
            />
            <AvatarFallback>
              {getInitials(invitation.org?.name || '')}
            </AvatarFallback>
          </Avatar>
        )}

        <CardTitle className="text-4xl font-medium">{t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-8 pb-0 pt-6">
        <p className="text-center text-xl">
          <Trans
            ns="org"
            i18nKey="orgInvitation.description"
            values={{
              user_name: user.user_metadata.name,
              org_name: invitation.org?.name,
            }}
            shouldUnescape
          />
        </p>
        <p className="text-center text-xl">{t('prompt')}</p>
      </CardContent>
      <CardFooter className="justify-center gap-12">
        <Button
          onClick={handleDecline}
          variant="outline"
          btnType="primary"
          className="w-40"
        >
          {t('declineButton')}
        </Button>
        <Button
          onClick={handleAccept}
          variant="greenGradient"
          btnType="primary"
          className="w-40"
        >
          {t('acceptButton')}
        </Button>
      </CardFooter>
    </Card>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <Background>
      <div className="fixed left-10 top-10">
        <LogoWithName />
      </div>
      <FlowLayout>
        <FlowLayoutBody>{children}</FlowLayoutBody>
      </FlowLayout>
    </Background>
  );
};

export const ManageOrgInvitation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('org', { keyPrefix: 'orgInvitation' });
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');

  const { data: user, isLoading: isUserLoading } = useUser();
  const {
    data: invitation,
    isLoading: isInvitationLoading,
    error: invitationError,
  } = useGetOrgInvitation({
    invitationToken: invitationToken || '',
    enabled: !!invitationToken && !!user,
  });
  const { isPending } = useManageOrgInvitationMutation({
    onSuccess: () => navigate(paths.home.getHref()),
  });

  if (!invitationToken) {
    return <ErrorCard title={t('errorTitle')} message={t('missingToken')} />;
  }

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user && !isUserLoading) {
      const redirectPath = paths.org.manageInvitation.getHref(invitationToken);
      navigate(paths.auth.login.getHref(redirectPath));
    }
  }, [user, isUserLoading, invitationToken, navigate]);

  // Redirect if invitation already processed
  useEffect(() => {
    const isProcessed =
      invitation?.status === 'accepted' || invitation?.status === 'declined';
    if (invitation && isProcessed) {
      navigate(paths.home.getHref());
    }
  }, [invitation, navigate]);

  return (
    <Layout>
      <Body
        isUserLoading={isUserLoading}
        isInvitationLoading={isInvitationLoading}
        isPending={isPending}
        invitation={invitation}
        invitationError={invitationError}
        user={user}
        invitationToken={invitationToken}
      />
    </Layout>
  );
};

const Body = ({
  isUserLoading,
  isInvitationLoading,
  isPending,
  invitation,
  invitationError,
  user,
  invitationToken,
}: {
  isUserLoading: boolean;
  isInvitationLoading: boolean;
  isPending: boolean;
  invitation: GetOrgInvitationResult | undefined;
  invitationError: Error | null | undefined;
  user: User | null | undefined;
  invitationToken: string;
}) => {
  const { t } = useTranslation('org', { keyPrefix: 'orgInvitation' });

  if (isUserLoading || isInvitationLoading || isPending) {
    return <LoadingCard />;
  }

  if (invitationError && !invitation) {
    return <ErrorCard title={t('errorTitle')} message={t('invalidToken')} />;
  }

  if (!invitation) {
    return <ErrorCard title={t('errorTitle')} message={t('fetchError')} />;
  }

  if (user?.email !== invitation.email) {
    return <ErrorCard title={t('errorTitle')} message={t('wrongEmail')} />;
  }

  return (
    <InvitationActions
      invitationToken={invitationToken}
      invitation={invitation}
      user={user}
    />
  );
};
